import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Printer, Undo2, ShieldCheck, ShieldX, ClipboardCheck, ListChecks } from "lucide-react";
import { api, idr, pesanGalat } from "../../../lib/api";
import { formatTanggal } from "../../../lib/tanggal";
import { useAuth } from "../../../context/AuthContext";
import InvoiceKreditPrint from "../../../components/faktur/InvoiceKreditPrint";
import {
  MDR_BASE, ST_SIAP, ST_SUDAH, Modal, DialogAlasan, StatusPill, kelasTombol, kelasTombolKecil,
} from "./shared";

/** Invoice Kredit per data kredit (C13) — isi datang dari server, layar cuma
 *  merender + `cetak()`. `sumber`: {url, params}. */
export function DialogInvoice({ sumber, onTutup }) {
  const [data, setData] = useState(null);
  // Pemanggil mengoper fungsi baru tiap render — lewat ref supaya pengambilan
  // data tidak diulang setiap kali halaman induk dirender ulang.
  const tutupRef = useRef(onTutup);
  tutupRef.current = onTutup;
  useEffect(() => {
    let batal = false;
    api.get(sumber.url, { params: sumber.params }).then((r) => { if (!batal) setData(r.data.item); })
      .catch((e) => { if (!batal) { toast.error(pesanGalat(e, "Gagal memuat Invoice Kredit")); tutupRef.current(); } });
    return () => { batal = true; };
  }, [sumber]);
  return (
    <Modal judul="Invoice Kredit" ikon={Printer} onTutup={onTutup} lebar="max-w-2xl" testid="mdr-invoice">
      {data ? <InvoiceKreditPrint data={data} /> : <p className="text-sm text-slate-400 py-6 text-center">Memuat...</p>}
    </Modal>
  );
}

/** "Perlu Review" karena lebih dari satu transaksi cocok (C8): sistem TIDAK
 *  memilih — finance memilih satu. Server memeriksa ulang bahwa pilihannya
 *  termasuk kandidat yang ia hitung saat ini (bukan nota sembarang).
 *  No. Kontrak yang diketik kasir di baris leasing nota (D3) ditampilkan di
 *  sebelah No. Kontrak berkas — pembeda paling pasti, tapi tetap finance yang
 *  memilih ("cocok" hanya penanda sama persis). */
export function DialogKandidat({ baris, hak, onTutup, onSelesai }) {
  const [busy, setBusy] = useState(false);
  const pilih = async (k) => {
    setBusy(true);
    try {
      const r = await api.post(`${MDR_BASE}/data/${baris.data_id}/pilih-kandidat`, { sale_id: k.sale_id, imei: k.imei });
      toast.success(r.data.message || "Pilihan tersimpan");
      onSelesai?.();
      onTutup();
    } catch (e) { toast.error(pesanGalat(e, "Gagal menyimpan pilihan")); }
    finally { setBusy(false); }
  };
  return (
    <Modal judul="Pilih Transaksi" ikon={ListChecks} onTutup={onTutup} lebar="max-w-3xl" testid="mdr-kandidat"
           sub={`Data claim ${baris.leasing || ""} ${idr(baris.nominal)} · ${formatTanggal(baris.tanggal_pembelian)}${baris.no_kontrak ? ` · No. Kontrak berkas ${baris.no_kontrak}` : ""} — lebih dari satu transaksi cocok, sistem tidak memilih sendiri.`}>
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr>
              <th className="text-left px-2 py-2">Nota</th><th className="text-left px-2 py-2">Tanggal</th>
              <th className="text-left px-2 py-2">Cabang</th><th className="text-left px-2 py-2">Pelanggan</th>
              <th className="text-left px-2 py-2">IMEI</th><th className="text-left px-2 py-2">Tipe</th>
              <th className="text-left px-2 py-2">No. Kontrak (nota)</th>
              <th className="text-left px-2 py-2">Tab</th><th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(baris.kandidat || []).map((k) => (
              <tr key={`${k.sale_id}-${k.imei}`} data-testid={`mdr-kandidat-${k.imei}`}>
                <td className="px-2 py-1.5 font-mono">{k.no_nota}</td>
                <td className="px-2 py-1.5">{formatTanggal(k.tanggal)}</td>
                <td className="px-2 py-1.5">{k.cabang || "-"}</td>
                <td className="px-2 py-1.5">{k.nama_pelanggan || "-"}</td>
                <td className="px-2 py-1.5 font-mono">{k.imei}</td>
                <td className="px-2 py-1.5">{k.tipe_produk || "-"}</td>
                <td className="px-2 py-1.5 font-mono" data-testid={`mdr-kandidat-kontrak-${k.imei}`}>
                  {(k.no_kontrak_nota || []).join(", ") || "-"}
                  {k.kontrak_cocok && (
                    <span className="ml-1 inline-flex px-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px] font-sans">
                      sama dengan berkas
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5">{k.tab || "-"}</td>
                <td className="px-2 py-1.5 text-right">
                  {hak.ubah && (
                    <button type="button" disabled={busy} onClick={() => pilih(k)} className={kelasTombolKecil}
                            data-testid={`mdr-kandidat-pilih-${k.imei}`}>Pilih</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

const LABEL_AKSI = {
  tandai_siap: "Ditandai Siap Claim", batal_siap: "Siap Claim dibatalkan", masuk_batch: "Masuk batch",
  keluar_batch: "Keluar dari batch", tandai_sudah: "Ditandai Sudah Claim", ajukan_batal: "Pengajuan batal",
  batal_disetujui: "Pembatalan disetujui", batal_ditolak: "Pengajuan batal ditolak", perlu_review: "Ditandai Perlu Review",
  tinjau_selesai: "Selesai ditinjau",
};

/** Detail satu klaim (Siap/Sudah Claim) — tujuan tautan Pusat Approval
 *  (`?klaim=<id>`). Semua tombol digerbang `can()` dengan aksi yang SAMA
 *  dengan endpoint-nya; "Setujui Batal" juga memakai `hak.setujui_batal`
 *  dari server = `boleh_setujui_batal` (SPV bukan pengaju), satu penjaga
 *  untuk endpoint & Pusat Approval. */
export function DialogKlaim({ klaimId, hak, onTutup, onBerubah }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [alasan, setAlasan] = useState(null);   // {aksi, judul, ...}
  const [invoice, setInvoice] = useState(null);
  const [busy, setBusy] = useState(false);

  const tutupRef = useRef(onTutup);
  tutupRef.current = onTutup;
  const muat = useCallback(async () => {
    try {
      const r = await api.get(`${MDR_BASE}/klaim/${klaimId}`);
      setData(r.data);
    } catch (e) { toast.error(pesanGalat(e, "Gagal memuat klaim")); tutupRef.current(); }
  }, [klaimId]);
  useEffect(() => { muat(); }, [muat]);

  const kirim = async (path, body, pesanBaku) => {
    setBusy(true);
    try {
      const r = await api.post(`${MDR_BASE}/klaim/${klaimId}/${path}`, body);
      toast.success(r.data.message || "Tersimpan");
      setAlasan(null);
      onBerubah?.();
      await muat();
    } catch (e) { toast.error(pesanGalat(e, pesanBaku)); }
    finally { setBusy(false); }
  };

  if (!data) {
    return (
      <Modal judul="Detail Klaim" ikon={FileText} onTutup={onTutup} testid="mdr-klaim">
        <p className="text-sm text-slate-400 py-6 text-center">Memuat...</p>
      </Modal>
    );
  }
  const k = data.item;
  const t = data.tampilan || {};
  const s = k.snapshot || {};
  const aj = k.pengajuan_batal;
  const pengaju = aj && ((aj.oleh_id && aj.oleh_id === user?.id)
    || (aj.oleh_email && (aj.oleh_email || "").toLowerCase() === (user?.email || "").toLowerCase()));
  const aktif = !!k.aktif;
  const cnBatch = data.cn_batch;

  const baris = [
    ["Tab", k.tab], ["Nota", s.no_nota], ["Tanggal Pembelian", formatTanggal(s.tanggal_pembelian)],
    ["Tanggal Aktivasi", formatTanggal(s.tanggal_aktivasi)], ["IMEI", s.imei], ["SN", s.sn],
    ["Tipe Produk", s.tipe_produk], ["Nama Konsumen", s.nama_konsumen], ["Cabang", s.cabang],
    ["Leasing/CC", s.leasing], ["Tenor", s.tenor ? `${s.tenor} bulan` : "-"], ["No. Kontrak", s.no_kontrak],
    ["SRP", s.srp != null ? idr(s.srp) : "-"], ["DP", s.dp != null ? idr(s.dp) : "-"], ["Nominal", idr(s.nominal)],
    ["Program PP", s.pp ? `${s.pp.program_code || ""} ${s.pp.nama_program || ""}` : "-"],
    ["Batch", k.batch_kode],
    ["Credit Note batch", cnBatch ? `${cnBatch.cn_code || "-"} · ${idr(cnBatch.nilai)} (${cnBatch.status_cn || "-"})` : "-"],
    ["Diterima setelah ditinjau", s.tinjauan ? `${s.tinjauan.catatan} — ${s.tinjauan.oleh_nama || s.tinjauan.oleh_email || ""}` : "-"],
  ];

  return (
    <Modal judul={`Klaim ${s.imei || ""}`} ikon={FileText} onTutup={onTutup} lebar="max-w-3xl" testid="mdr-klaim"
           sub={`Dibuat ${formatTanggal(k.created_at, { denganJam: true })} oleh ${k.created_by_name || k.created_by || "-"}`}>
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {aktif ? <StatusPill status={t.status} testid="mdr-klaim-status" />
                 : <span className="text-xs font-medium text-slate-500" data-testid="mdr-klaim-status">Dibatalkan</span>}
          {t.status !== k.status && aktif && <span className="text-xs text-slate-500">(status klaim: <StatusPill status={k.status} />)</span>}
        </div>

        {k.perlu_review && (
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-xs text-amber-900" data-testid="mdr-klaim-review">
            <p className="font-semibold">Perlu Review</p>
            <p>{k.perlu_review.teks}</p>
          </div>
        )}
        {aj && (
          <div className="border border-rose-200 bg-rose-50 rounded-lg p-3 text-xs text-rose-900" data-testid="mdr-klaim-pengajuan">
            <p className="font-semibold">Pengajuan batal menunggu persetujuan SPV</p>
            <p>Oleh {aj.oleh_nama || aj.oleh_email} · {formatTanggal(aj.at, { denganJam: true })}</p>
            <p>Alasan: {aj.alasan}</p>
            {/* Nilai CN batch diisi manual (C5) — sistem tidak tahu apakah unit
                ini termasuk nilainya. Penyetuju harus melihatnya sebelum
                memutuskan; membatalkan klaim TIDAK mengubah CN itu. */}
            {cnBatch?.hidup && (
              <p className="mt-1 font-medium text-amber-900" data-testid="mdr-klaim-cn-batch">
                Batch {cnBatch.batch_kode} sudah punya Credit Note {cnBatch.cn_code} {idr(cnBatch.nilai)}. Kalau unit ini
                termasuk nilai CN itu, sesuaikan/batalkan CN-nya di menu Credit Note — membatalkan klaim ini tidak mengubah CN.
              </p>
            )}
          </div>
        )}

        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs">
          {baris.map(([l, v]) => (
            <div key={l} className="flex justify-between gap-3 border-b border-slate-100 py-1">
              <dt className="text-slate-500">{l}</dt><dd className="font-medium text-right break-all">{v || "-"}</dd>
            </div>
          ))}
        </dl>

        <div className="flex flex-wrap gap-2">
          {aktif && (
            <button type="button" className={kelasTombol} data-testid="mdr-klaim-invoice"
                    onClick={() => setInvoice({ url: `${MDR_BASE}/klaim/${klaimId}/invoice` })}>
              <Printer className="w-4 h-4" /> Cetak Invoice Kredit
            </button>
          )}
          {hak.ubah && aktif && k.status === ST_SIAP && !k.batch_id && (
            <button type="button" className={kelasTombol} data-testid="mdr-klaim-batal-siap"
                    onClick={() => setAlasan({ path: "batal-siap", judul: "Batalkan Siap Claim?", labelYa: "Batalkan Siap Claim",
                                               bahaya: true, pesan: "Gagal membatalkan Siap Claim",
                                               keterangan: "Data PP & nota dilepas; baris ini kembali dihitung ulang." })}>
              <Undo2 className="w-4 h-4" /> Batalkan Siap Claim
            </button>
          )}
          {hak.ubah && aktif && k.status === ST_SUDAH && !aj && (
            <button type="button" className={kelasTombol} data-testid="mdr-klaim-ajukan-batal"
                    onClick={() => setAlasan({ path: "ajukan-batal", judul: "Ajukan pembatalan Sudah Claim", labelYa: "Ajukan",
                                               pesan: "Gagal mengajukan pembatalan",
                                               keterangan: "Sudah Claim hanya bisa dibatalkan dengan persetujuan SPV (pemegang izin Hapus Claim MDR) yang bukan pengaju." })}>
              <Undo2 className="w-4 h-4" /> Ajukan Batal
            </button>
          )}
          {hak.hapus && aj && data.hak?.setujui_batal && (
            <button type="button" disabled={busy} data-testid="mdr-klaim-setujui-batal"
                    onClick={() => kirim("setujui-batal", {}, "Gagal menyetujui pembatalan")}
                    className="h-10 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              <ShieldCheck className="w-4 h-4" /> Setujui Batal
            </button>
          )}
          {aj && (hak.hapus || pengaju) && hak.ubah && (
            <button type="button" className={kelasTombol} data-testid="mdr-klaim-tolak-batal"
                    onClick={() => setAlasan({ path: "tolak-batal", judul: pengaju && !hak.hapus ? "Tarik pengajuan batal?" : "Tolak pengajuan batal?",
                                               labelYa: pengaju && !hak.hapus ? "Tarik Pengajuan" : "Tolak", wajib: false,
                                               pesan: "Gagal menolak pengajuan" })}>
              <ShieldX className="w-4 h-4" /> {pengaju && !hak.hapus ? "Tarik Pengajuan" : "Tolak Pengajuan"}
            </button>
          )}
          {hak.ubah && aktif && k.perlu_review && (
            <button type="button" className={kelasTombol} data-testid="mdr-klaim-tinjau"
                    onClick={() => setAlasan({ path: "tinjau-selesai", judul: "Tandai selesai ditinjau", label: "Catatan hasil tinjauan",
                                               labelYa: "Selesai Ditinjau", pesan: "Gagal mencatat tinjauan" })}>
              <ClipboardCheck className="w-4 h-4" /> Selesai Ditinjau
            </button>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Riwayat</p>
          <ul className="text-xs space-y-1" data-testid="mdr-klaim-riwayat">
            {(k.riwayat || []).map((r, i) => (
              <li key={i} className="flex flex-wrap gap-x-2">
                <span className="text-slate-400 font-mono">{formatTanggal(r.at, { denganJam: true })}</span>
                <span className="font-medium">{LABEL_AKSI[r.aksi] || r.aksi}</span>
                {r.batch_kode && <span className="font-mono">{r.batch_kode}</span>}
                {r.cn_code && <span className="font-mono">CN {r.cn_code}</span>}
                <span className="text-slate-500">{r.oleh}</span>
                {r.alasan && <span className="text-slate-600">— {r.alasan}</span>}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {alasan && (
        <DialogAlasan judul={alasan.judul} keterangan={alasan.keterangan} label={alasan.label} labelYa={alasan.labelYa}
                      wajib={alasan.wajib !== false} bahaya={alasan.bahaya} testid={`mdr-klaim-${alasan.path}`}
                      onYa={(teks) => kirim(alasan.path, { alasan: teks }, alasan.pesan)} onTutup={() => setAlasan(null)} />
      )}
      {invoice && <DialogInvoice sumber={invoice} onTutup={() => setInvoice(null)} />}
    </Modal>
  );
}
